/**
 * pipeline.ts — Vertrag für `src/data/generated/pipeline.json`.
 *
 * Erzeugt von `scripts/build-data.ts` (npm run data) aus
 * inventory.json × houseData.ts × runs.json × artifacts.json.
 *
 * Regel: Jede Zahl, die eine Oberfläche zeigt, steht hier drin. Kein Aggregat
 * im React-Code, keine vorausgezählte Konstante.
 */

export type LaneId = "sources" | "mirror" | "knowledge" | "generators" | "output" | "deployment";
export type FlowState = "ok" | "running" | "failed" | "sealed" | "open" | "skipped" | "ghost";
export type ReleaseKey = string;            // "202604" | "202610" | "202710"
export const ALL: ReleaseKey = "all";

export interface PipelineLane {
  id: LaneId; label: string; sub: string;
  metricLabel: string;                       // "Quellen", "Artefakte", …
  metric: Record<ReleaseKey, number>;        // inkl. "all"
  aggregate: Record<ReleaseKey, Partial<Record<FlowState, number>>>;
}
export interface PipelineNode {
  id: string; lane: LaneId; label: string; sub: string;
  componentId?: string;                      // Bezug nach houseData
  state: Record<ReleaseKey, FlowState>;
  value: Record<ReleaseKey, number | null>;  // null = kein Wert für diesen Stand
  releaseChips?: {
    release: ReleaseKey; count: number; consultation: boolean; drillDown?: string;
    // ── additiv (Bedeutung der vier Felder oben unverändert) ──────────────
    /** Artefakt-Typ des Chips, z. B. "ahb" — ein Chip ist ein Typ in einem Stand. */
    type?: string;
    /** Anzeigename des Typs ("AHB"), damit die Oberfläche keine eigene Tabelle braucht. */
    label?: string;
  }[];
  goalRef?: string;                          // z. B. "repo:maco-regu-parse"
}

/** Bequemer Alias für einen einzelnen Release-Chip. */
export type PipelineReleaseChip = NonNullable<PipelineNode["releaseChips"]>[number];
export interface PipelineEdge {
  from: string; to: string;
  derived: boolean;                          // true = aus Freitext heuristisch aufgelöst
  releases: ReleaseKey[];                    // in welchen Ständen die Kante gilt
}
export interface PipelineData {
  generated_at: string;
  releases: ReleaseKey[];
  lanes: PipelineLane[];
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  runsByRelease: Record<ReleaseKey, string[]>;
}

// ── Ergänzungen (additiv, keine Änderung an den oben deklarierten Feldern) ───

/** Einheit einer Leitkennzahl — Kurzform für `aria-label`-Sätze im Bahnkopf. */
export const LANE_ORDER: LaneId[] = [
  "sources", "mirror", "knowledge", "generators", "output", "deployment",
];

/**
 * Roll-up-Priorität einer Zustandsmenge (aus dense übernommen):
 * failed > sealed > running > open > ok. `skipped`/`ghost` sind Nicht-Ereignisse
 * und tragen die Zeile nur, wenn nichts anderes da ist.
 */
export const STATE_PRIORITY: FlowState[] = [
  "failed", "sealed", "running", "open", "ok", "skipped", "ghost",
];

export function rollUpState(states: FlowState[]): FlowState | null {
  for (const s of STATE_PRIORITY) if (states.includes(s)) return s;
  return null;
}

// ── Artefakt-Typen ───────────────────────────────────────────────────────────

/** Anzeigenamen der Artefakt-Typen (Schlüssel = Ordner der knowledge-collection). */
export const ARTIFACT_TYPE_LABEL: Record<string, string> = {
  ebds: "EBDs",
  ahb: "AHB",
  mig: "MIG",
  pruefidentifikatoren: "Prüfidentifikatoren",
  codelisten: "Codelisten",
  schemas: "Schemas",
  "bnetza-festlegung": "BNetzA-Prozesse",
  "bo4e-schema": "BO4E-Schemas",
};

/**
 * Artefakt-Typen mit einer Detailseite unter `/artifacts/:type`
 * (Router: `artifacts/:type` → `ArtifactTypePage`). Alle anderen Typen bekommen
 * bewusst **kein** Drill-Down-Ziel — die Zahl bleibt sichtbar, der Klick geht
 * aber nicht ins Leere. Einzige Quelle dieser Liste, damit Datenschritt und
 * Oberflächen nicht auseinanderlaufen.
 */
export const ARTIFACT_DRILLDOWN_TYPES: string[] = [
  "ebds", "ahb", "mig", "pruefidentifikatoren", "codelisten",
];

export function artifactDrillDown(type: string): string | undefined {
  return ARTIFACT_DRILLDOWN_TYPES.includes(type) ? `/artifacts/${type}` : undefined;
}
